import { DeleteIcon, DuplicatePlusIcon } from '@/assets/icons';
import { PropertiesTitle } from '@common/components/PropertiesTitle';
import FlipIcon from '@mui/icons-material/Flip';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import { Stack } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Trans } from 'react-i18next';

import { useAppStore } from '@/modules/app/store/useAppStore';
import { StyledTooltip } from './StyledTooltip';
import { OnChangeHandler } from './inputs';
import { theme } from '../theme';

type Props = {
  name: string;
  onDuplicateMultiClick?: () => void;
  onMirrorHorizontalClick?: () => void;
  onMirrorVerticalClick?: () => void;
  onRotate90Click?: () => void;
  onDeleteClick: () => void;
  disabled?: boolean;
  editable?: boolean;
  onBlur?: () => void;
  onNameChange?: OnChangeHandler<string>;
  onFocus?: () => void;
};

const iconBtnStyle = {
  border: 0,
  p: '2px',
  '&:hover': {
    color: theme.palette.neutral.darker,
  },
  '&:active:hover': {
    backgroundColor: theme.palette.shades.light,
    color: theme.palette.neutral.darker,
    borderColor: 'transparent',
  },
};

export function PropertiesHeader({
  name,
  disabled,
  editable = true,
  onBlur,
  onDuplicateMultiClick,
  onMirrorVerticalClick,
  onMirrorHorizontalClick,
  onRotate90Click,
  onDeleteClick,
  onFocus,
  onNameChange,
}: Props) {
  const mode = useAppStore((state) => state.mode);

  const isDisabled = disabled ?? mode !== 'EDITABLE';
  const titleIsEditable = !isDisabled && editable;

  return (
    <Stack
      alignItems='center'
      direction='row'
      justifyContent='flex-start'
      gap={1}
      sx={{ pb: 1, pt: 2, px: 2 }}
    >
      <PropertiesTitle
        value={name}
        editable={titleIsEditable}
        onBlur={titleIsEditable ? onBlur : undefined}
        onChange={titleIsEditable ? onNameChange : undefined}
        onFocus={titleIsEditable ? onFocus : undefined}
        multiline
        maxRowsPassive={2}
        maxRowsActive={2}
        sx={{ flex: '1 1 auto' }}
      />

      {onDuplicateMultiClick && (
        <StyledTooltip
          placement='bottom'
          disabled={isDisabled}
          title={
            <Trans
              i18nKey='duplicate_tooltip'
              ns='common'
              components={{
                s: <span />,
              }}
            >
              Grid copy
            </Trans>
          }
        >
          <IconButton
            sx={{ ...iconBtnStyle, ml: 1 }}
            disabled={isDisabled}
            onClick={isDisabled ? undefined : onDuplicateMultiClick}
            className='btn-icon'
            disableRipple={isDisabled}
          >
            <DuplicatePlusIcon />
          </IconButton>
        </StyledTooltip>
      )}

      {onMirrorVerticalClick && (
        <StyledTooltip placement='bottom' disabled={isDisabled} title='Mirror vertically'>
          <IconButton
            sx={iconBtnStyle}
            disabled={isDisabled}
            onClick={isDisabled ? undefined : onMirrorVerticalClick}
            className='btn-icon'
            disableRipple={isDisabled}
          >
            <FlipIcon fontSize='small' />
          </IconButton>
        </StyledTooltip>
      )}

      {onMirrorHorizontalClick && (
        <StyledTooltip placement='bottom' disabled={isDisabled} title='Mirror horizontally'>
          <IconButton
            sx={iconBtnStyle}
            disabled={isDisabled}
            onClick={isDisabled ? undefined : onMirrorHorizontalClick}
            className='btn-icon'
            disableRipple={isDisabled}
          >
            <FlipIcon fontSize='small' sx={{ transform: 'rotate(90deg)' }} />
          </IconButton>
        </StyledTooltip>
      )}

      {onRotate90Click && (
        <StyledTooltip placement='bottom' disabled={isDisabled} title='Rotate 90° clockwise'>
          <IconButton
            sx={iconBtnStyle}
            disabled={isDisabled}
            onClick={isDisabled ? undefined : onRotate90Click}
            className='btn-icon'
            disableRipple={isDisabled}
          >
            <Rotate90DegreesCwIcon fontSize='small' />
          </IconButton>
        </StyledTooltip>
      )}

      <StyledTooltip
        placement='bottom'
        disabled={isDisabled}
        title={
          <Trans
            i18nKey='delete_tooltip'
            ns='common'
            components={{
              s: <span />,
            }}
          >
            Delete <span>Backspace</span>
          </Trans>
        }
      >
        <IconButton
          onClick={isDisabled ? undefined : onDeleteClick}
          className='btn-icon'
          disabled={isDisabled}
          sx={iconBtnStyle}
          disableRipple={isDisabled}
        >
          <DeleteIcon />
        </IconButton>
      </StyledTooltip>
    </Stack>
  );
}
